import { useEffect, useState } from "react";

/**
 * A custom hook to manage state synchronized with localStorage.
 * @param key The key under which the state is stored in localStorage.
 * @param initialValue The initial value of the state.
 * @returns A tuple containing the state and a function to update it.
 */
export const useAsyncStorageState = <T>(key: string, initialValue: T) => {
  const [state, setState] = useState<T>(() => {
    if (typeof window === "undefined") return initialValue;
    try {
      const storedValue = localStorage.getItem(key);
      return storedValue !== null ? JSON.parse(storedValue) : initialValue;
    } catch (error) {
      console.error(`Error parsing localStorage key "${key}":`, error);
      return initialValue;
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(key, JSON.stringify(state));
    } catch (error) {
      console.error(`Error setting localStorage key "${key}":`, error);
    }
  }, [key, state]);

  return [state, setState] as const;
};
